import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import Navbar from "../../components/common/navbar";
import StatsBox from "../../components/common/statsBoxv2";
// import AdBoxComponent from "../../components/common/adBox";
import { useNavigate } from 'react-router-dom';
import constant from '../../constant';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

function AdBox({ title, description }) {
  return (
    <div className="bg-white p-4 rounded-lg shadow-md">
      <h3 className="font-semibold text-lg mb-2">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  );
}

const ImageModal = ({ src, onClose }) => {
  if (!src) return null;

  return (
    // Backdrop
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50 p-4"
      onClick={onClose}
    >
      {/* Modal Content */}
      <div
        className="bg-white p-4 rounded-lg shadow-xl relative max-w-6xl max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute -top-2 -right-2 bg-white rounded-full p-1 text-gray-700 hover:text-black text-2xl leading-none"
          aria-label="Close modal"
          style={{ width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} // Style for better button appearance
        >
          × {/* HTML entity for 'X' */}
        </button>
        {/* Image */}
        <img
          src={src}
          alt="Analytics Graph Full Size"
          className="w-full h-full object-contain max-h-[85vh]"
        />
      </div>
    </div>
  );
};


const DashboardContent = () => {
  // --- State Variables ---
  const [selectedPeriod, setSelectedPeriod] = useState({
    from: new Date(new Date().getFullYear(), 0, 1),
    to: new Date(),
  });
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal visibility
  const navigate = useNavigate();

  const constructApiUrl = (endpoint) => {
    const userRole = localStorage.getItem("role");
    const adminSelectedUser = localStorage.getItem("adminSelectedUser");
    let apiUrl = `${constant.REACT_APP_API_URL}${endpoint}`;
    if (userRole === "admin" && adminSelectedUser) {
      apiUrl = `${constant.REACT_APP_API_URL}${endpoint}${endpoint.includes("?") ? "&" : "?"
        }userId=${adminSelectedUser}`;
    }
    return apiUrl;
  };

  // --- Fetch Data Effect ---
  useEffect(() => {
    const fetchDashboardDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          alert('No authentication token found');
          navigate('/login');
          return;
        }

        const apiUrl = constructApiUrl("/user/dashboard");

        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const message = await response.text();
          throw new Error("Please select an user");
        }


        const result = await response.json();
        if (result.success) {
          setDashboardData(result.data);
        } else {
          setError(result.message || 'Failed to fetch dashboard data');
        }

      } catch (error) {
        setError(error.message || 'An error occurred while fetching dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardDetails();
  }, [navigate]);


  // --- Chart Data and Options (Static Fallback) ---
  const chartData = {
    labels: ["January", "February", "March", "April", "May", "June"],
    datasets: [
      {
        label: "Total Receivable",
        data: [1200, 1500, 2100, 1800, 2200, 2500],
        fill: false,
        borderColor: "rgba(75, 192, 192, 1)",
        tension: 0.3,
        borderWidth: 2,
        pointBackgroundColor: "rgba(75, 192, 192, 1)",
        pointRadius: 5,
        pointHoverRadius: 7,
      },
      {
        label: "Total Payable",
        data: [800, 1200, 1600, 1400, 1500, 1700],
        fill: false,
        borderColor: "rgba(255, 99, 132, 1)",
        tension: 0.3,
        borderWidth: 2,
        pointBackgroundColor: "rgba(255, 99, 132, 1)",
        pointRadius: 5,
        pointHoverRadius: 7,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false, // Allow chart to fill container height better
    plugins: {
      title: {
        display: true,
        text: "Receivable vs Payable (Monthly)",
        font: {
          size: 16,
        },
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            return `${tooltipItem.raw.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return `$${value.toLocaleString()}`;
          },
        },
      },
    },
  };

  // --- Event Handlers ---
  const handleFromDateChange = (e) => {
    setSelectedPeriod((prev) => ({ ...prev, from: new Date(e.target.value) }));
  };

  const handleToDateChange = (e) => {
    setSelectedPeriod((prev) => ({ ...prev, to: new Date(e.target.value) }));
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // --- Render Logic ---
  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-yellow-500 font-bold text-xl text-center h-screen uppercase flex justify-center items-center">{error}</div>;
  }


  return (
    <>
      <div className="px-6 pt-20 md:pt-4 pb-8">
        <Navbar title="Dashboard" />

        <div className="max-w-8xl mx-auto lg:px-4 grid grid-cols-12 gap-6 my-8">
          {/* Left Column */}
          <div className="col-span-12 md:col-span-9">

            {/* Stats Section */}
            <div className="grid grid-cols-12 md:gap-6 gap-4 mb-8">
              <StatsBox title="Total Receivable (to collect)" text={dashboardData?.totalReceivable || 0}></StatsBox>
              <StatsBox title="Total Payable (to pay)" text={dashboardData?.totalPayable || 0}></StatsBox>
            </div>

            {/* Transaction Section */}
            <div className="mb-8">
              <div className="bg-white rounded-lg shadow-md">
                {/* <h3 className="font-semibold text-lg mb-2">Transaction</h3>
    <p className="text-sm text-gray-600 mb-4">Transaction details overview</p> */}

                <div className="overflow-x-auto h-60 overflow-y-scroll">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Voucher Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Party Name</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Credit</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Debit</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dashboardData.transactionData.map((transaction) => (
                        <tr key={transaction._id}>
                          <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">
                            {new Date(transaction.transactionDate).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">
                            {transaction.voucherType}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">
                            {transaction.partyId?.name || 'N/A'}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700 text-right">
                            {transaction.credit}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700 text-right">
                            {transaction.debit}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>


            {/* Graph Section */}
            <div className="w-full flex flex-col">
              {dashboardData?.analyticsGraph ? (
                <>
                  <div
                    className="flex-grow flex justify-center items-center cursor-pointer overflow-hidden"
                    onClick={openModal}
                    title="Click to enlarge graph"
                  >
                    <img
                      src={dashboardData.analyticsGraph}
                      alt="Analytics Graph Preview"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                </>
              ) : (
                <>
                  <h3 className="font-semibold text-lg mb-2 text-center">No Data</h3>
                </>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div className="col-span-12 md:col-span-3 space-y-4">
            {dashboardData?.ads && dashboardData.ads.length > 0 ? (
              dashboardData.ads.map((ad, index) => (
                <AdBox key={index} title={ad.title} description={ad.description} />
              ))
            ) : (
              <div className="bg-white p-4 rounded-lg shadow-md text-center text-gray-500">
                No ads available.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Render Modal conditionally */}
      {isModalOpen && (
        <ImageModal
          src={dashboardData?.analyticsGraph}
          onClose={closeModal}
        />
      )}
    </>
  );
};

export default DashboardContent;